import { useState } from 'react';
import { useCurrentUser } from '@/features/users/hooks';
import { useArchiveNotification, useGetNotifications, useMarkAsRead } from '../../hooks';
import { UserContext } from '../../types';
import { Button, Chip, Icon, StandardCard, Typography } from '@/components';
import { NotificationMessage } from '../messages';
import { Skeleton } from '@/components/ui/skeleton';
interface NotificationsListProps {
  context?: UserContext;
  pollingInterval?: number;
}

export function NotificationsCard({ context, pollingInterval = 60000 }: NotificationsListProps) {
  const currentUser = useCurrentUser();
  const [showArchived, setShowArchived] = useState(false);

  const { id: userId } = currentUser;

  const { activeNotifications, archivedNotifications, isLoading, hasNotifications, unreadCount } = useGetNotifications({
    userId,
    context,
    pollingInterval,
  });

  const displayedNotifications = showArchived ? archivedNotifications : activeNotifications;

  const { markAsRead } = useMarkAsRead({ userId });
  const { archiveNotification } = useArchiveNotification({ userId });

  const handleMarkAsRead = async (notificationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      markAsRead(notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleArchive = async (notificationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      archiveNotification(notificationId);
    } catch (error) {
      console.error('Error archiving notification:', error);
    }
  };

  if (!hasNotifications)
    return (
      <StandardCard title="Alerts">
        <Typography as="p" className="text-muted-foreground py-4 text-center text-sm">
          No notifications for now
        </Typography>
      </StandardCard>
    );

  return (
    <StandardCard
      title={
        <div className="flex justify-between">
          <div className="flex items-center">
            <Typography className="font-heading mr-2" size="body-1" weight="semibold">
              Alerts
            </Typography>
            <Chip className="px-2 text-sm text-red-800" color="error">
              {unreadCount > 99 ? '99+' : unreadCount}
            </Chip>
          </div>
          <div>
            <Button size="sm" color="neutral" onClick={() => setShowArchived(!showArchived)}>
              <Typography size="caption" weight="semibold" className="font-heading">
                {showArchived ? 'Show Active' : 'Show Archived'}
              </Typography>
            </Button>
          </div>
        </div>
      }
    >
      {isLoading ? (
        <Skeleton className="mt-1 h-8 w-full sm:ml-1" />
      ) : (
        <div className="max-h-[200px] overflow-y-auto pr-2">
          <div className="space-y-2">
            {displayedNotifications.length === 0 ? (
              <Typography as="p" className="text-muted-foreground py-4 text-center text-sm">
                {showArchived ? 'No archived alerts' : 'No active alerts'}
              </Typography>
            ) : (
              displayedNotifications.map((notification) => {
                return (
                  <div
                    key={notification.id}
                    className={`group relative flex items-center gap-3 rounded-lg border px-3 py-2 transition-all ${
                      !notification.isRead
                        ? 'bg-primary-50 border-primary-200 hover:bg-primary-100'
                        : 'bg-background border-border hover:bg-grey-25'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start gap-2">
                        <NotificationMessage notification={notification} />
                      </div>
                    </div>

                    <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      {!notification.isRead && (
                        <Button
                          color="ghostPrimary"
                          onClick={(e) => handleMarkAsRead(notification.id, e)}
                          className="h-7 w-7 border-0 bg-transparent"
                        >
                          <Icon name="check" className="h-5 w-5" />
                        </Button>
                      )}
                      {notification.status !== 'archived' && (
                        <Button
                          color="ghostPrimary"
                          onClick={(e) => handleArchive(notification.id, e)}
                          className="h-7 w-7 border-0 bg-transparent"
                        >
                          <Icon name="archive" className="h-5 w-5" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </StandardCard>
  );
}
